import { useMemo } from "react";
import {
  type ColScaffold,
  type RowScaffold,
  Scaffold,
  type ScaffoldDropZone,
} from "../../scaffold";
import { TLF_VIEW_MODES } from "../../utility";
import { ArsListingCell } from "./ArsListingCell";
import { ArsListingContextMenuProvider } from "./ArsListingContextMenuProvider";
import { useArsListing } from "./ArsListingContextProvider";
import { ArsListingDraggableSidebar } from "./ArsListingDraggableSidebar";
import { arsListingZoneColorResolver } from "./arsListingZoneColorResolver";
import { buildApplyDroppedColumnsAction } from "./buildApplyDroppedColumnsAction";
import { buildScaffoldCellData } from "./buildScaffoldCellData";
import { buildScaffoldColumns } from "./buildScaffoldColumns";
import { ARSL_SET_COLUMNS, ARSL_SET_ROW_HEIGHT, HEADER_ROW_ID } from "./interface";

/**
 * Renders ArsListing content inside ArsListingContextProvider.
 * Wrapper to handle the updates from Scaffold
 *
 * @returns ArsListing table and draggable variable sidebar
 */
export const ArsListingContent = <T,>(): React.ReactElement => {
  const { state, dispatch } = useArsListing<T>();
  const arsListingBody = state?.bodyData ?? { id: "", arsListingColumns: [], arsBodyRows: [] };
  const id = state?.settings.id ?? "ars-listing";
  const disabled = state?.settings.disabled ?? false;
  const justification = state?.settings.justification;

  const arsListingColumns = arsListingBody.arsListingColumns;
  const scaffoldColumns = buildScaffoldColumns(arsListingColumns);

  const tlfViewMode =
    state?.settings.tlfViewMode ??
    // istanbul ignore next
    TLF_VIEW_MODES[0];

  const scaffoldCellData = state
    ? buildScaffoldCellData(
        arsListingColumns,
        arsListingBody.arsBodyRows,
        state.settings.editorProps,
        tlfViewMode,
      )
    : undefined;

  const scaffoldRows = useMemo<RowScaffold<T>[]>(() => {
    const headerRow: RowScaffold<T> = {
      id: HEADER_ROW_ID,
      height: arsListingColumns[0]?.headerHeight ?? 40,
      rowGroupNumber: 0,
      rowNumber: 1,
      type: "header",
      textAlignment: "left",
    };
    const bodyRows: RowScaffold<T>[] = arsListingBody.arsBodyRows.map((bodyRow, i) => ({
      id: bodyRow.id,
      height: bodyRow.rowHeight,
      rowGroupNumber: 1,
      rowNumber: i + 2,
      type: "body",
      textAlignment: "left",
    }));
    return [headerRow, ...bodyRows];
  }, [arsListingBody.arsBodyRows, arsListingColumns]);

  /**
   * Handles variable drops on scaffold cells.
   *
   * @param event - Drop event
   * @param rowId - Target scaffold row id
   * @param colId - Target scaffold column id
   * @param zone - Classified drop zone within the target cell
   */
  const handleScaffoldDrop = (
    event: React.DragEvent<HTMLDivElement>,
    rowId: string,
    colId: string,
    zone: ScaffoldDropZone,
  ): void => {
    if (state && !disabled && rowId === HEADER_ROW_ID) {
      event.preventDefault();

      const jsonPayload = event.dataTransfer.getData("application/json");

      const action = buildApplyDroppedColumnsAction(
        jsonPayload,
        colId,
        zone,
        state.settings.editorProps.textToT,
      );

      if (action !== null) {
        dispatch(action);
      }
    }
  };

  /**
   * Handles column width changes from user resize operations.
   *
   * @param colId - Column id being resized
   * @param col - Updated column definition with new width
   */
  const handleChangeCol = (colId: string, col: ColScaffold<T>): void => {
    if (!disabled) {
      const updatedColumns = arsListingColumns.map((column) =>
        column.id === colId
          ? {
              ...column,
              columnWidth: col.width,
            }
          : column,
      );
      dispatch({
        operation: ARSL_SET_COLUMNS,
        columns: updatedColumns,
      });
    }
  };

  /**
   * Handles row height changes from user resize operations.
   *
   * @param rowId - Row id being resized
   * @param row - Updated row definition with new height
   */
  const handleChangeRow = (rowId: string, row: RowScaffold<T>): void => {
    if (!disabled) {
      if (rowId === HEADER_ROW_ID) {
        dispatch({
          operation: ARSL_SET_COLUMNS,
          columns: arsListingColumns.map((column) => ({ ...column, headerHeight: row.height })),
        });
      } else {
        const bodyRowIndex = arsListingBody.arsBodyRows.findIndex((r) => r.id === rowId);
        /* istanbul ignore else -- scaffold only passes rowIds built from arsBodyRows */
        if (bodyRowIndex !== -1) {
          dispatch({
            operation: ARSL_SET_ROW_HEIGHT,
            rowId,
            rowHeight: row.height,
          });
        }
      }
    }
  };

  return !state || !scaffoldCellData ? (
    <></>
  ) : (
    <div
      className="d-flex flex-row"
      style={{ width: "100%", minWidth: 0, minHeight: "500px" }}
    >
      <div
        className="flex-grow-1"
        style={{ minWidth: 0 }}
      >
        <ArsListingContextMenuProvider selectedColumns={arsListingColumns}>
          <Scaffold<T>
            id={id}
            disabled={disabled}
            justification={justification}
            cellComponent={{
              ...state.settings.editorProps,
              Editor: ArsListingCell<T>,
            }}
            cellData={scaffoldCellData}
            cols={scaffoldColumns}
            rows={scaffoldRows}
            onDrop={handleScaffoldDrop}
            onChangeCol={handleChangeCol}
            onChangeRow={handleChangeRow}
            zoneColors={arsListingZoneColorResolver}
          />
        </ArsListingContextMenuProvider>
      </div>
      <ArsListingDraggableSidebar />
    </div>
  );
};

ArsListingContent.displayName = "ArsListingContent";
